import React, { useEffect, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading1,
  Heading2,
  Heading3,
  ImageIcon,
  Italic,
  List,
  ListOrdered,
  Minus,
  Plus,
  QrCode,
  Quote,
  Redo2,
  Strikethrough,
  Undo2,
} from 'lucide-react';
import {
  LETTER_TEMPLATE_PLACEHOLDER_OWNER_KEYS,
  LETTER_TEMPLATE_PLACEHOLDER_PROPERTY_KEYS,
  letterTemplatePlaceholderLabel,
} from '../adminProspectLetterTemplateDisplay.js';
import { LETTER_TEMPLATE_FONT_FAMILIES } from './LetterFontExtension.js';
import {
  LETTER_TEMPLATE_DEFAULT_FONT_SIZE_PT,
  LETTER_TEMPLATE_FONT_SIZES,
} from './LetterFontSizeExtension.js';
import { LETTER_TEMPLATE_IMAGE_ALLOWED_MIME_TYPES } from './letterTemplateImageUpload.js';

function ToolButton({ title, onClick, active, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!!disabled}
      title={title}
      aria-label={title}
      className={`rounded p-1.5 ${
        active ? 'bg-slate-300 text-slate-900' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function resolveActiveFontFamily(editor) {
  if (!editor) return '';
  for (const family of LETTER_TEMPLATE_FONT_FAMILIES) {
    if (family === 'helvetica') {
      if (!editor.isActive('letterFont')) return 'helvetica';
    } else if (editor.isActive('letterFont', { family })) {
      return family;
    }
  }
  const attrs = editor.getAttributes('letterFont');
  if (attrs?.family && LETTER_TEMPLATE_FONT_FAMILIES.includes(attrs.family)) {
    return attrs.family;
  }
  return '';
}

function resolveActiveFontSize(editor) {
  if (!editor) return LETTER_TEMPLATE_DEFAULT_FONT_SIZE_PT;
  if (!editor.isActive('letterFontSize')) return LETTER_TEMPLATE_DEFAULT_FONT_SIZE_PT;
  const pt = Number(editor.getAttributes('letterFontSize')?.pt);
  return LETTER_TEMPLATE_FONT_SIZES.includes(pt) ? pt : LETTER_TEMPLATE_DEFAULT_FONT_SIZE_PT;
}

export default function LetterTemplateToolbar({
  editor,
  t,
  readOnly,
  hasQrBlock,
  qrInsertDisabled,
  imageInsertDisabled,
  imageUploading,
  canInsertPlaceholder,
  onInsertPlaceholder,
  onImageFileSelected,
}) {
  const [variableOpen, setVariableOpen] = useState(false);
  const popoverRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!variableOpen) return undefined;
    const handleClick = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setVariableOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [variableOpen]);

  if (!editor || readOnly) return null;

  const insertPlaceholder = (key) => {
    if (!canInsertPlaceholder) return;
    if (typeof onInsertPlaceholder === 'function') {
      onInsertPlaceholder(key);
    } else {
      editor.chain().focus().insertLetterPlaceholder(key).run();
    }
    setVariableOpen(false);
  };

  const activeFont = resolveActiveFontFamily(editor);
  const activeFontSize = resolveActiveFontSize(editor);
  const qrDisabled = hasQrBlock || qrInsertDisabled;

  const handleFontChange = (event) => {
    const family = event.target.value;
    if (!family) return;
    editor.chain().focus().setLetterFont(family).run();
  };

  const handleFontSizeChange = (event) => {
    const pt = Number(event.target.value);
    if (!Number.isFinite(pt)) return;
    editor.chain().focus().setLetterFontSize(pt).run();
  };

  const handleImageClick = () => {
    if (imageInsertDisabled || imageUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || typeof onImageFileSelected !== 'function') return;
    onImageFileSelected(file);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1 border-b border-slate-200 pb-2"
      role="toolbar"
      aria-label={t('admin.prospection.letterTemplate.toolbar.label')}
    >
      <label className="flex items-center gap-1 text-xs">
        <span className="font-medium text-slate-600">
          {t('admin.prospection.letterTemplate.toolbar.font')}
        </span>
        <select
          value={activeFont || ''}
          onChange={handleFontChange}
          className="rounded border border-slate-300 px-1 py-0.5 text-xs"
          aria-label={t('admin.prospection.letterTemplate.toolbar.font')}
        >
          <option value="" disabled>
            —
          </option>
          {LETTER_TEMPLATE_FONT_FAMILIES.map((family) => (
            <option key={family} value={family}>
              {t(`admin.prospection.letterTemplate.font.${family}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-1 text-xs">
        <span className="font-medium text-slate-600">
          {t('admin.prospection.letterTemplate.toolbar.fontSize')}
        </span>
        <select
          value={activeFontSize}
          onChange={handleFontSizeChange}
          className="rounded border border-slate-300 px-1 py-0.5 text-xs"
          aria-label={t('admin.prospection.letterTemplate.toolbar.fontSize')}
        >
          {LETTER_TEMPLATE_FONT_SIZES.map((pt) => (
            <option key={pt} value={pt}>
              {pt} pt
            </option>
          ))}
        </select>
      </label>
      <span className="mx-0.5 h-6 w-px bg-slate-200" aria-hidden />
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.bold')}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.italic')}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.strike')}
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="h-4 w-4" aria-hidden />
      </ToolButton>
      <span className="mx-0.5 h-6 w-px bg-slate-200" aria-hidden />
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.h1')}
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.h2')}
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.h3')}
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" aria-hidden />
      </ToolButton>
      <span className="mx-0.5 h-6 w-px bg-slate-200" aria-hidden />
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.bulletList')}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.orderedList')}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.blockquote')}
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.horizontalRule')}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus className="h-4 w-4" aria-hidden />
      </ToolButton>
      <span className="mx-0.5 h-6 w-px bg-slate-200" aria-hidden />
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.alignLeft')}
        active={editor.isActive({ align: 'left' })}
        onClick={() => editor.chain().focus().setLetterAlign('left').run()}
      >
        <AlignLeft className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.alignCenter')}
        active={editor.isActive({ align: 'center' })}
        onClick={() => editor.chain().focus().setLetterAlign('center').run()}
      >
        <AlignCenter className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.alignRight')}
        active={editor.isActive({ align: 'right' })}
        onClick={() => editor.chain().focus().setLetterAlign('right').run()}
      >
        <AlignRight className="h-4 w-4" aria-hidden />
      </ToolButton>
      <span className="mx-0.5 h-6 w-px bg-slate-200" aria-hidden />
      <div className="relative" ref={popoverRef}>
        <ToolButton
          title={t('admin.prospection.letterTemplate.toolbar.insertVariable')}
          disabled={!canInsertPlaceholder}
          onClick={() => setVariableOpen((open) => !open)}
        >
          <span className="flex items-center gap-1 text-xs font-medium">
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {t('admin.prospection.letterTemplate.toolbar.insertVariable')}
          </span>
        </ToolButton>
        {variableOpen ? (
          <div className="absolute left-0 top-full z-20 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
            <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.letterTemplate.placeholder.category.owner')}
            </p>
            {LETTER_TEMPLATE_PLACEHOLDER_OWNER_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className="block w-full rounded px-2 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-100"
                onClick={() => insertPlaceholder(key)}
              >
                {letterTemplatePlaceholderLabel(key, t)}
              </button>
            ))}
            <p className="mt-2 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.letterTemplate.placeholder.category.property')}
            </p>
            {LETTER_TEMPLATE_PLACEHOLDER_PROPERTY_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className="block w-full rounded px-2 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-100"
                onClick={() => insertPlaceholder(key)}
              >
                {letterTemplatePlaceholderLabel(key, t)}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <ToolButton
        title={
          qrDisabled
            ? t('admin.prospection.letterTemplate.toolbar.qrMaxOne')
            : t('admin.prospection.letterTemplate.toolbar.insertQr')
        }
        disabled={qrDisabled}
        onClick={() => editor.chain().focus().insertLetterQrBlock().run()}
      >
        <span className="flex items-center gap-1 text-xs font-medium">
          <QrCode className="h-3.5 w-3.5" aria-hidden />
          {t('admin.prospection.letterTemplate.toolbar.insertQr')}
        </span>
      </ToolButton>
      <ToolButton
        title={
          imageInsertDisabled
            ? t('admin.prospection.letterTemplate.toolbar.imageMax')
            : t('admin.prospection.letterTemplate.toolbar.insertImage')
        }
        disabled={imageInsertDisabled || imageUploading}
        onClick={handleImageClick}
      >
        <span className="flex items-center gap-1 text-xs font-medium">
          <ImageIcon className="h-3.5 w-3.5" aria-hidden />
          {imageUploading
            ? t('admin.prospection.letterTemplate.image.uploading')
            : t('admin.prospection.letterTemplate.toolbar.insertImage')}
        </span>
      </ToolButton>
      <input
        ref={fileInputRef}
        type="file"
        accept={LETTER_TEMPLATE_IMAGE_ALLOWED_MIME_TYPES.join(',')}
        className="hidden"
        onChange={handleFileChange}
        aria-hidden
        tabIndex={-1}
      />
      <span className="mx-0.5 h-6 w-px bg-slate-200" aria-hidden />
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.undo')}
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="h-4 w-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t('admin.prospection.letterTemplate.toolbar.redo')}
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="h-4 w-4" aria-hidden />
      </ToolButton>
    </div>
  );
}
